import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTenant } from '../context/TenantContext';
import {
  LeadFieldConfig,
  LeadFieldConfigCreate,
  LeadPatientFieldMapping,
  LeadStatusConfig,
  leadFieldConfigService,
  leadPatientMappingService,
  leadStatusConfigService,
} from '../services/leadConfigService';
import leadService, { LeadFieldOption } from '../services/leadService';
import { patientService } from '../services/patientService';

/**
 * Two screens that belong together, on two tabs.
 *
 * "Fields" is the lead twin of PatientFieldConfigPage: the extra fields a
 * tenant wants on a lead, which is what makes the entity generic rather than
 * a Clalit referral form.
 *
 * "Patient Field Mapping" is what those fields become when a lead converts.
 * It is explicit on purpose: nothing at all is copied from a lead onto the
 * patient unless there is a row here saying so. Copying by matching key name
 * would look helpful right up until a patient gains a value nobody asked it to
 * gain, and by then nobody remembers why.
 */

type Tab = 'fields' | 'mapping';

const FIELD_TYPES = ['text', 'number', 'date', 'bool', 'single_select'] as const;

const labelToKey = (label: string) =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500';
const primaryButton =
  'px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors';
const cancelButton =
  'px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors';
const thClass =
  'px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider';

export const LeadFieldConfigPage: React.FC = () => {
  const { currentTenant } = useTenant();
  const [tab, setTab] = useState<Tab>('fields');
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Lead Fields</h1>

        <div className="flex gap-6 border-b border-gray-200 mb-6">
          {([['fields', 'Fields'], ['mapping', 'Patient Field Mapping']] as [Tab, string][])
            .map(([value, label]) => (
              <button
                key={value}
                onClick={() => setTab(value)}
                className={`pb-2 text-sm ${
                  tab === value
                    ? 'font-semibold text-teal-700 border-b-2 border-teal-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {!currentTenant ? (
          <div className="text-gray-500">Select a tenant to configure lead fields.</div>
        ) : tab === 'fields' ? (
          <FieldsTab tenantId={currentTenant.id} onError={setError} />
        ) : (
          <MappingTab tenantId={currentTenant.id} onError={setError} />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------

const emptyForm = (): LeadFieldConfigCreate => ({
  key: '',
  label: '',
  field_type: 'text',
  options: null,
  display_in_overview: true,
  display_in_edit: true,
  display_condition: null,
  default_value: null,
  is_required: false,
  validation: null,
  edit_tab_name: 'Overview',
  edit_tab_order: 0,
  sort_order: 0,
});

const FieldsTab: React.FC<{ tenantId: number; onError: (m: string | null) => void }> = ({
  tenantId, onError,
}) => {
  const [configs, setConfigs] = useState<LeadFieldConfig[]>([]);
  const [statuses, setStatuses] = useState<LeadStatusConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState<LeadFieldConfigCreate>(emptyForm());
  // Stops the key being rewritten from the label once somebody has typed one.
  const [keyTouched, setKeyTouched] = useState(false);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const [fields, statusConfigs] = await Promise.all([
        leadFieldConfigService.getAll(tenantId),
        leadStatusConfigService.getAll(tenantId),
      ]);
      setConfigs(fields);
      setStatuses(statusConfigs);
      onError(null);
    } catch (err) {
      console.error('Failed to load lead field configs:', err);
      onError('Failed to load lead field configs');
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, onError]);

  useEffect(() => { load(); }, [load]);

  const set = <K extends keyof LeadFieldConfigCreate>(k: K, v: LeadFieldConfigCreate[K]) =>
    setForm(prev => ({ ...prev, [k]: v }));

  const setValidation = (k: string, v: any) =>
    setForm(prev => {
      const next = { ...(prev.validation ?? {}) };
      if (v === '' || v === null || v === undefined) delete next[k];
      else next[k] = v;
      return { ...prev, validation: Object.keys(next).length ? next : null };
    });

  const reset = () => {
    setIsAdding(false);
    setEditingId(null);
    setForm(emptyForm());
    setKeyTouched(false);
  };

  const handleAdd = () => {
    reset();
    setIsAdding(true);
    setForm({ ...emptyForm(), sort_order: configs.length });
  };

  const handleEdit = (config: LeadFieldConfig) => {
    setIsAdding(false);
    setEditingId(config.id);
    setKeyTouched(true);
    setForm({
      key: config.key,
      label: config.label,
      field_type: config.field_type,
      options: config.options,
      display_in_overview: config.display_in_overview,
      display_in_edit: config.display_in_edit,
      display_condition: config.display_condition,
      default_value: config.default_value,
      is_required: config.is_required,
      validation: config.validation,
      edit_tab_name: config.edit_tab_name,
      edit_tab_order: config.edit_tab_order,
      sort_order: config.sort_order,
    });
  };

  const handleLabelChange = (label: string) => {
    setForm(prev => ({
      ...prev,
      label,
      key: keyTouched ? prev.key : labelToKey(label),
    }));
  };

  const handleSave = async () => {
    if (!form.key.trim() || !form.label.trim()) return;
    const payload: LeadFieldConfigCreate = {
      ...form,
      key: form.key.trim(),
      label: form.label.trim(),
      // Options only mean anything for a select; leaving stale ones behind on a
      // field that was switched to text makes the stored config lie.
      options: form.field_type === 'single_select' ? (form.options ?? []) : null,
    };
    try {
      if (isAdding) await leadFieldConfigService.create(tenantId, payload);
      else if (editingId) await leadFieldConfigService.update(editingId, payload);
      await load();
      reset();
    } catch (err) {
      console.error('Failed to save lead field config:', err);
      onError('Failed to save lead field config');
    }
  };

  const handleDelete = async (config: LeadFieldConfig) => {
    if (!window.confirm(
      `Delete "${config.label}"? Values already stored on leads are left in place but ` +
      `will no longer be shown anywhere.`,
    )) return;
    try {
      await leadFieldConfigService.delete(config.id);
      await load();
    } catch (err) {
      console.error('Failed to delete lead field config:', err);
      onError('Failed to delete lead field config');
    }
  };

  const options = form.options ?? [];
  const setOption = (i: number, patch: Partial<{ value: string; label: string }>) =>
    set('options', options.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));

  const validation = form.validation ?? {};
  const conditionValues = form.display_condition?.values ?? [];

  if (isLoading) return <div className="flex justify-center items-center h-64">Loading...</div>;

  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={handleAdd} className={primaryButton}>+ Add Lead Field</button>
      </div>

      {(isAdding || editingId) && (
        <div className="mb-6 p-4 border border-gray-300 rounded-md bg-gray-50">
          <h3 className="text-lg font-semibold mb-4">
            {isAdding ? 'Add New Lead Field' : 'Edit Lead Field'}
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
              <input type="text" value={form.label} onChange={(e) => handleLabelChange(e.target.value)}
                     className={inputClass} placeholder="e.g. Referral Source" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Key</label>
              <input type="text" value={form.key}
                     onChange={(e) => { setKeyTouched(true); set('key', e.target.value); }}
                     className={inputClass} placeholder="e.g. referral_source" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
              <select value={form.field_type} onChange={(e) => set('field_type', e.target.value)}
                      className={inputClass}>
                {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input type="number" value={form.sort_order ?? 0}
                     onChange={(e) => set('sort_order', Number(e.target.value))} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Edit Dialog Tab</label>
              <input type="text" value={form.edit_tab_name ?? ''}
                     onChange={(e) => set('edit_tab_name', e.target.value || null)}
                     className={inputClass} placeholder="Overview, or a new tab name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tab Order</label>
              <input type="number" value={form.edit_tab_order ?? 0}
                     onChange={(e) => set('edit_tab_order', Number(e.target.value))} className={inputClass} />
            </div>
          </div>

          {form.field_type === 'single_select' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
              {options.map((opt, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input type="text" value={opt.label} placeholder="Label"
                         onChange={(e) => setOption(i, { label: e.target.value })} className={inputClass} />
                  <input type="text" value={opt.value} placeholder="Value"
                         onChange={(e) => setOption(i, { value: e.target.value })} className={inputClass} />
                  <button onClick={() => set('options', options.filter((_, idx) => idx !== i))}
                          className="px-3 text-red-600 hover:text-red-900">Remove</button>
                </div>
              ))}
              <button onClick={() => set('options', [...options, { value: '', label: '' }])}
                      className="text-sm text-teal-600 hover:text-teal-900">+ Add option</button>
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Value</label>
              <input type="text" value={form.default_value ?? ''}
                     onChange={(e) => set('default_value', e.target.value === '' ? null : e.target.value)}
                     className={inputClass} placeholder="Filled in on new leads" />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.display_in_overview ?? true}
                     onChange={(e) => set('display_in_overview', e.target.checked)}
                     className="h-4 w-4 text-teal-600 border-gray-300 rounded" />
              Show in overview
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.display_in_edit ?? true}
                     onChange={(e) => set('display_in_edit', e.target.checked)}
                     className="h-4 w-4 text-teal-600 border-gray-300 rounded" />
              Show in the edit dialog
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.is_required ?? false}
                     onChange={(e) => set('is_required', e.target.checked)}
                     className="h-4 w-4 text-teal-600 border-gray-300 rounded" />
              Required
            </label>
          </div>

          {statuses.length > 0 && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display only for specific statuses
              </label>
              <div className="flex flex-wrap gap-4">
                {statuses.map((s) => (
                  <label key={s.key} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={conditionValues.includes(s.key)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...conditionValues, s.key]
                          : conditionValues.filter(v => v !== s.key);
                        set('display_condition', next.length ? { field: 'status', values: next } : null);
                      }}
                      className="h-4 w-4 text-teal-600 border-gray-300 rounded"
                    />
                    {s.title}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Validation</label>
            <div className="grid grid-cols-3 gap-4">
              {form.field_type === 'text' && (
                <>
                  <input type="number" placeholder="Min length" value={validation.min_length ?? ''}
                         onChange={(e) => setValidation('min_length', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                         className={inputClass} />
                  <input type="number" placeholder="Max length" value={validation.max_length ?? ''}
                         onChange={(e) => setValidation('max_length', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                         className={inputClass} />
                  <input type="text" placeholder="Regex" value={validation.regex ?? ''}
                         onChange={(e) => setValidation('regex', e.target.value)} className={inputClass} />
                </>
              )}
              {form.field_type === 'number' && (
                <>
                  <input type="number" placeholder="Min value" value={validation.min_value ?? ''}
                         onChange={(e) => setValidation('min_value', e.target.value === '' ? '' : parseFloat(e.target.value))}
                         className={inputClass} />
                  <input type="number" placeholder="Max value" value={validation.max_value ?? ''}
                         onChange={(e) => setValidation('max_value', e.target.value === '' ? '' : parseFloat(e.target.value))}
                         className={inputClass} />
                </>
              )}
              {form.field_type === 'date' && (
                <>
                  <select value={validation.type ?? ''} onChange={(e) => setValidation('type', e.target.value)}
                          className={inputClass}>
                    <option value="">No date rule</option>
                    <option value="past_date">Must be in the past</option>
                    <option value="future_date">Must be in the future</option>
                    <option value="within_x_days">Within X days</option>
                    <option value="min_age">Minimum age</option>
                  </select>
                  {(validation.type === 'within_x_days' || validation.type === 'min_age') && (
                    <input type="number" value={validation.value ?? ''}
                           placeholder={validation.type === 'within_x_days' ? 'Days' : 'Years'}
                           onChange={(e) => setValidation('value', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                           className={inputClass} />
                  )}
                </>
              )}
              <input type="text" placeholder="Error message" value={validation.error_message ?? ''}
                     onChange={(e) => setValidation('error_message', e.target.value)} className={inputClass} />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button onClick={handleSave} className={primaryButton}>Save</button>
            <button onClick={reset} className={cancelButton}>Cancel</button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className={thClass}>Label</th>
              <th className={thClass}>Key</th>
              <th className={thClass}>Type</th>
              <th className={thClass}>Tab</th>
              <th className={thClass}>Required</th>
              <th className={thClass}>Order</th>
              <th className={`${thClass} text-right`}>Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {configs.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  No lead fields yet. Click "Add Lead Field" to create one.
                </td>
              </tr>
            ) : configs.map((config) => (
              <tr key={config.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{config.label}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{config.key}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{config.field_type}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{config.edit_tab_name || 'Overview'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{config.is_required ? 'Yes' : ''}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{config.sort_order}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button onClick={() => handleEdit(config)} className="text-teal-600 hover:text-teal-900 mr-4">Edit</button>
                  <button onClick={() => handleDelete(config)} className="text-red-600 hover:text-red-900">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Lead -> patient mapping
// ---------------------------------------------------------------------------

const MappingTab: React.FC<{ tenantId: number; onError: (m: string | null) => void }> = ({
  tenantId, onError,
}) => {
  const [mappings, setMappings] = useState<LeadPatientFieldMapping[]>([]);
  const [leadFields, setLeadFields] = useState<LeadFieldOption[]>([]);
  const [patientFields, setPatientFields] = useState<{ name: string; label: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [leadField, setLeadField] = useState('');
  const [patientField, setPatientField] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const [rows, lf, pf] = await Promise.all([
        leadPatientMappingService.getAll(tenantId),
        // The field option lists must be the configured tenant's, not the
        // operator's own - a system user has no tenant of their own, so without
        // this the lead list 400s and the patient list comes back empty.
        leadService.getLeadFields(tenantId),
        patientService.getPatientFields(undefined, tenantId),
      ]);
      setMappings(rows);
      setLeadFields(lf);
      setPatientFields(pf);
      onError(null);
    } catch (err) {
      console.error('Failed to load lead to patient mappings:', err);
      onError('Failed to load lead to patient mappings');
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, onError]);

  useEffect(() => { load(); }, [load]);

  const labelFor = useMemo(() => {
    const lead: Record<string, string> = {};
    leadFields.forEach(f => { lead[f.name] = f.label; });
    const patient: Record<string, string> = {};
    patientFields.forEach(f => { patient[f.name] = f.label; });
    return { lead, patient };
  }, [leadFields, patientFields]);

  // A lead field already mapped is not offered again: the table allows one
  // destination per lead field, so a second row would only fail on save.
  const mappedLeadFields = useMemo(
    () => new Set(mappings.filter(m => m.id !== editingId).map(m => m.lead_field)),
    [mappings, editingId],
  );

  const reset = () => {
    setLeadField('');
    setPatientField('');
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!leadField || !patientField) return;
    try {
      if (editingId) {
        await leadPatientMappingService.update(editingId, {
          lead_field: leadField, patient_field: patientField,
        });
      } else {
        await leadPatientMappingService.create(tenantId, {
          lead_field: leadField, patient_field: patientField, sort_order: mappings.length,
        });
      }
      await load();
      reset();
    } catch (err) {
      console.error('Failed to save mapping:', err);
      onError('Failed to save mapping');
    }
  };

  const handleDelete = async (mapping: LeadPatientFieldMapping) => {
    if (!window.confirm(
      `Stop copying "${labelFor.lead[mapping.lead_field] ?? mapping.lead_field}" onto the patient? ` +
      `Patients already created keep the value they were given.`,
    )) return;
    try {
      await leadPatientMappingService.delete(mapping.id);
      await load();
    } catch (err) {
      console.error('Failed to delete mapping:', err);
      onError('Failed to delete mapping');
    }
  };

  if (isLoading) return <div className="flex justify-center items-center h-64">Loading...</div>;

  return (
    <>
      <p className="text-sm text-gray-600 mb-4">
        What a lead carries onto the patient created from it. Nothing is copied unless it is
        mapped here - not even fields whose names match.
      </p>

      <div className="mb-6 p-4 border border-gray-300 rounded-md bg-gray-50">
        <div className="grid grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Lead field</label>
            <select value={leadField} onChange={(e) => setLeadField(e.target.value)} className={inputClass}>
              <option value="">Select a lead field</option>
              {leadFields
                .filter(f => f.name === leadField || !mappedLeadFields.has(f.name))
                .map(f => <option key={f.name} value={f.name}>{f.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Patient field</label>
            <select value={patientField} onChange={(e) => setPatientField(e.target.value)} className={inputClass}>
              <option value="">Select a patient field</option>
              {patientFields.map(f => <option key={f.name} value={f.name}>{f.label}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} className={primaryButton} disabled={!leadField || !patientField}>
              {editingId ? 'Update' : 'Add mapping'}
            </button>
            {editingId && <button onClick={reset} className={cancelButton}>Cancel</button>}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className={thClass}>Lead field</th>
              <th className={thClass}>Patient field</th>
              <th className={`${thClass} text-right`}>Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {mappings.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-6 py-4 text-center text-gray-500">
                  Nothing is copied from a lead onto the patient yet.
                </td>
              </tr>
            ) : mappings.map((mapping) => (
              <tr key={mapping.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {labelFor.lead[mapping.lead_field] ?? mapping.lead_field}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {labelFor.patient[mapping.patient_field] ?? mapping.patient_field}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => {
                      setEditingId(mapping.id);
                      setLeadField(mapping.lead_field);
                      setPatientField(mapping.patient_field);
                    }}
                    className="text-teal-600 hover:text-teal-900 mr-4"
                  >
                    Edit
                  </button>
                  <button onClick={() => handleDelete(mapping)} className="text-red-600 hover:text-red-900">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default LeadFieldConfigPage;
